/**
 * Agent Debug Feeds — shared types.
 * Wire shape matches docs/AGENT_DEBUG_FEEDS_DESIGN.md §6.
 */

export type AgentDebugSource = 'agent-caller' | 'officer-assist' | 'agent-backend' | 'test' | string;
export type AgentDebugLevel = 'debug' | 'info' | 'warn' | 'error';

/** One debug event on the wire (`event: history` carries an array; `event: debug` carries one). */
export interface AgentDebugEvent {
  id: string;
  ts: number; // epoch ms
  callId: string;
  source: AgentDebugSource;
  feed: string;
  kind: string;
  turnId?: string;
  level?: AgentDebugLevel;
  payload: unknown;
}

/**
 * Binary blob embedded in a payload (image/video/audio). Rendered inline by
 * `<MediaBlob>`. If `_truncated`, `_b64` is a downscaled thumbnail/clip and the
 * full version is fetchable via `GET /api/debug/agent-event/{id}?callId=...`.
 */
export interface MediaBlob {
  _b64: string;
  _mime: string;
  _bytes: number;
  _w?: number;
  _h?: number;
  _truncated?: boolean;
  _label?: string;
}

export function isMediaBlob(x: unknown): x is MediaBlob {
  return (
    !!x &&
    typeof x === 'object' &&
    typeof (x as Record<string, unknown>)._b64 === 'string' &&
    typeof (x as Record<string, unknown>)._mime === 'string'
  );
}

/** A truncated string field stand-in: `{ _truncated: true, _len, _preview }`. */
export function isTruncatedField(x: unknown): x is { _truncated: true; _len: number; _preview?: string } {
  return (
    !!x &&
    typeof x === 'object' &&
    (x as Record<string, unknown>)._truncated === true &&
    typeof (x as Record<string, unknown>)._len === 'number'
  );
}

/** True if anything anywhere in `payload` is a truncated field or a truncated blob. */
export function payloadHasTruncation(payload: unknown, depth = 0): boolean {
  if (depth > 12 || payload == null || typeof payload !== 'object') return false;
  if (isTruncatedField(payload)) return true;
  if (isMediaBlob(payload) && payload._truncated) return true;
  if (Array.isArray(payload)) return payload.some((v) => payloadHasTruncation(v, depth + 1));
  return Object.values(payload as Record<string, unknown>).some((v) => payloadHasTruncation(v, depth + 1));
}

/** Collect every MediaBlob found anywhere in a payload (depth-limited). */
export function findMediaBlobs(payload: unknown, depth = 0, acc: MediaBlob[] = []): MediaBlob[] {
  if (depth > 12 || payload == null || typeof payload !== 'object') return acc;
  if (isMediaBlob(payload)) {
    acc.push(payload);
    return acc;
  }
  if (Array.isArray(payload)) {
    for (const v of payload) findMediaBlobs(v, depth + 1, acc);
    return acc;
  }
  for (const v of Object.values(payload as Record<string, unknown>)) findMediaBlobs(v, depth + 1, acc);
  return acc;
}
